import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { isAuthorized } from '../../components/AuthorizedAction.js';
import DuplicationToasts from '../../components/projectDuplications/DuplicationToasts.jsx';
import { pushRoute } from '../../lib/router.js';
import {
  selectCurrentOrganizationId,
  selectIsSignedIn,
  selectUserPermissions,
} from '../../modules/auth.js';
import {
  hideProjectDuplication,
  retrieveProjectDuplications,
  selectFinishedDuplications,
  selectProcessingDuplications,
} from '../../modules/projectDuplications.js';

const POLL_INTERVAL_MS = 5000;

const mapStateToProps = (state) => ({
  isSignedIn: selectIsSignedIn(state),
  organizationId: selectCurrentOrganizationId(state),
  permissions: selectUserPermissions(state),
  processingCount: selectProcessingDuplications(state).length,
  finishedDuplications: selectFinishedDuplications(state),
});

const mapDispatchToProps = {
  retrieveProjectDuplications,
  hideProjectDuplication,
};

// Follows the duplications the user started: polls while a copy is running
// and shows the result until the user dismisses it, also after a page reload.
const ProjectDuplicationsWatcher = ({
  isSignedIn,
  organizationId,
  permissions,
  processingCount,
  finishedDuplications,
  retrieveProjectDuplications,
  hideProjectDuplication,
}) => {
  const canDuplicate =
    isSignedIn &&
    !!organizationId &&
    isAuthorized('create', 'project', permissions);

  useEffect(() => {
    if (canDuplicate) retrieveProjectDuplications().catch(() => {});
  }, [canDuplicate, organizationId]);

  useEffect(() => {
    if (!canDuplicate || !processingCount) return undefined;

    const poll = () => {
      if (document.visibilityState === 'visible') {
        retrieveProjectDuplications().catch(() => {});
      }
    };

    // A hidden tab is not polled; it catches up as soon as it is shown again
    const timer = setInterval(poll, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', poll);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [canDuplicate, processingCount]);

  const onClose = ({ id }) => hideProjectDuplication(id).catch(() => {});

  const onOpenProject = (duplication) => {
    onClose(duplication);
    pushRoute('projects/show', { projectId: duplication.targetProjectId });
  };

  if (!canDuplicate) return null;

  return (
    <DuplicationToasts
      duplications={finishedDuplications}
      onOpenProject={onOpenProject}
      onClose={onClose}
    />
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProjectDuplicationsWatcher);
